import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

const STORAGE_KEY = 'schedulerDownloadPending';
const LEGACY_STORAGE_KEY = 'systemLogExportPending';
const POLL_INTERVAL_MS = 5000;

/** Identifiers for scheduler-generated downloadable files. Add new kinds here. */
export const SCHEDULER_DOWNLOAD_TYPES = {
  SYSTEM_LOG_EXPORT: 'systemLogExport',
  TABLE_CSV_EXPORT: 'tableCsvExport',
  TABLE_PDF_EXPORT: 'tablePdfExport',
};

/**
 * Per-type download endpoint, i18n keys and UI events.
 * Register a new entry when the scheduler starts producing another downloadable file.
 */
export const SCHEDULER_DOWNLOAD_TYPE_CONFIG = {
  [SCHEDULER_DOWNLOAD_TYPES.SYSTEM_LOG_EXPORT]: {
    endpointKey: 'auditDownloadSystemlogExport',
    updatedEvent: 'system-log-export-updated',
    messages: {
      ready: 'audit.systemLog.exportReadyClick',
      failed: 'audit.systemLog.exportFailed',
      download: 'audit.systemLog.download',
      downloadError: 'audit.systemLog.exportDownloadError',
      expired: 'solicitacoesRelatorios.exportExpired',
    },
    defaultFilename: (idProcesso) => `systemlog-${idProcesso}.csv`,
  },
  [SCHEDULER_DOWNLOAD_TYPES.TABLE_CSV_EXPORT]: {
    endpointKey: 'exportDownloadTableCsv',
    messages: {
      ready: 'system.labels.csvExportReadyClick',
      failed: 'system.labels.csvExportFailed',
      download: 'system.labels.csvExportDownload',
      downloadError: 'system.labels.csvExportDownloadError',
      expired: 'solicitacoesRelatorios.exportExpired',
    },
    defaultFilename: (idProcesso) => `table-export-${idProcesso}.csv`,
  },
  [SCHEDULER_DOWNLOAD_TYPES.TABLE_PDF_EXPORT]: {
    endpointKey: 'exportDownloadTableCsv',
    messages: {
      ready: 'system.labels.pdfExportReadyClick',
      failed: 'system.labels.pdfExportFailed',
      download: 'system.labels.pdfExportDownload',
      downloadError: 'system.labels.pdfExportDownloadError',
      expired: 'solicitacoesRelatorios.exportExpired',
    },
    defaultFilename: (idProcesso) => `table-export-${idProcesso}.pdf`,
  },
};

function readRaw(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

function writePending(list) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function migrateLegacyPending() {
  const legacy = readRaw(LEGACY_STORAGE_KEY);
  if (!legacy.length) return;
  const current = readRaw(STORAGE_KEY);
  const byId = new Map(current.map((item) => [item.id_processo, item]));
  for (const item of legacy) {
    if (!item || !item.id_processo) continue;
    if (byId.has(item.id_processo)) continue;
    byId.set(item.id_processo, {
      id_processo: item.id_processo,
      type: SCHEDULER_DOWNLOAD_TYPES.SYSTEM_LOG_EXPORT,
      dti: item.dti || null,
      dtf: item.dtf || null,
      enqueuedAt: item.enqueuedAt || Date.now(),
    });
  }
  writePending(Array.from(byId.values()));
  sessionStorage.removeItem(LEGACY_STORAGE_KEY);
}

function readPending() {
  migrateLegacyPending();
  return readRaw(STORAGE_KEY);
}

export function getSchedulerDownloadTypeConfig(type) {
  return SCHEDULER_DOWNLOAD_TYPE_CONFIG[type] || null;
}

export function getPendingSchedulerDownloads() {
  return readPending();
}

/**
 * @param {object} entry
 * @param {number|string} entry.id_processo
 * @param {string} entry.type - one of SCHEDULER_DOWNLOAD_TYPES
 * @param {object} [entry.meta] - optional type-specific metadata
 */
export function addPendingSchedulerDownload(entry) {
  if (!entry || !entry.id_processo || !entry.type) return;
  if (!SCHEDULER_DOWNLOAD_TYPE_CONFIG[entry.type]) {
    console.warn('[schedulerDownloadPending] unknown type:', entry.type);
    return;
  }
  const list = readPending().filter((item) => item.id_processo !== entry.id_processo);
  const { id_processo, type, enqueuedAt, meta, ...rest } = entry;
  list.push({
    id_processo,
    type,
    meta: meta || null,
    enqueuedAt: enqueuedAt || Date.now(),
    ...rest,
  });
  writePending(list);
}

export function removePendingSchedulerDownload(idProcesso) {
  writePending(readPending().filter((item) => item.id_processo !== idProcesso));
}

export function hasPendingSchedulerDownloads() {
  return readPending().length > 0;
}

/**
 * Builds apiController.request options for a scheduler blob download.
 * @param {string} type - one of SCHEDULER_DOWNLOAD_TYPES
 * @param {string|number} idProcesso
 * @param {(key: string) => string} translate
 * @returns {object|null}
 */
export function buildSchedulerDownloadRequestOptions(type, idProcesso, translate) {
  const typeConfig = getSchedulerDownloadTypeConfig(type);
  if (!typeConfig) {
    return null;
  }

  const t = typeof translate === 'function' ? translate : (k) => k;
  const messages = typeConfig.messages || {};
  const defaultFilename =
    typeof typeConfig.defaultFilename === 'function'
      ? typeConfig.defaultFilename(idProcesso)
      : typeConfig.defaultFilename || `download-${idProcesso}`;

  const emitDownloadError = async (payload) => {
    let data = payload;
    if (payload && typeof payload.text === 'function') {
      try {
        data = JSON.parse(await payload.text());
      } catch (_err) {
        data = null;
      }
    }
    const expired =
      data &&
      (data.codigo === 'EXPORT_EXPIRED' ||
        /no longer available/i.test(String(data.mensagem || '')));
    EventBus.$emit('show-alert', {
      mensagem: expired
        ? t(messages.expired || 'solicitacoesRelatorios.exportExpired')
        : (data && data.mensagem) || t(messages.downloadError),
      tipo: 'error',
    });
  };

  return {
    data: { id_processo: idProcesso },
    config: { responseType: 'blob' },
    silent: true,
    onSuccess: async (response) => {
      const disposition = (response.headers && response.headers['content-disposition']) || '';
      const ct = ((response.headers && response.headers['content-type']) || '')
        .split(';')[0]
        .trim()
        .toLowerCase();

      const isAttachment = /attachment/i.test(disposition);
      if (!isAttachment && ct === 'application/json') {
        await emitDownloadError(response.data);
        return;
      }

      const filename =
        (disposition &&
          /filename="?([^"]+)"?/i.exec(disposition) &&
          /filename="?([^"]+)"?/i.exec(disposition)[1]) ||
        defaultFilename;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: async (error) => {
      const responseData = error && error.response && error.response.data;
      if (responseData) {
        await emitDownloadError(responseData);
        return;
      }
      EventBus.$emit('show-alert', {
        mensagem: t(messages.downloadError),
        tipo: 'error',
      });
    },
  };
}

/**
 * Downloads a completed scheduler export file for the given download type.
 * @param {string} type - one of SCHEDULER_DOWNLOAD_TYPES
 * @param {string|number} idProcesso
 * @param {(key: string) => string} translate - i18n function (e.g. this.$t)
 */
export function downloadSchedulerFile(type, idProcesso, translate) {
  const typeConfig = getSchedulerDownloadTypeConfig(type);
  if (!typeConfig) {
    return;
  }

  const endpoint = apiController.endpoints[typeConfig.endpointKey];
  if (!endpoint) {
    console.warn('[schedulerDownloadPending] missing download endpoint for type:', type);
    return;
  }

  const options = buildSchedulerDownloadRequestOptions(type, idProcesso, translate);
  if (!options) {
    return;
  }

  apiController.request(endpoint, options);
}

export { POLL_INTERVAL_MS, STORAGE_KEY };
